import React, { useState, useEffect } from 'react';

const Search = ({ reservations, selectedDate, onClose }) => {
  const [searchDate, setSearchDate] = useState('');
  const [searchTime, setSearchTime] = useState('');
  const [availabilityResults, setAvailabilityResults] = useState(null);

  // Check availability for specific date/time
  useEffect(() => {
    if (searchDate && searchTime) {
      const isBooked = reservations[searchDate] && reservations[searchDate][searchTime];
      setAvailabilityResults({
        date: searchDate,
        time: searchTime,
        isAvailable: !isBooked,
        reservation: isBooked || null
      });
    } else {
      setAvailabilityResults(null);
    }
  }, [searchDate, searchTime, reservations]);

  const formatFullDate = (dateStr) => {
    const date = new Date(dateStr);
    // Albanian day and month names
    const days = ['E Diel', 'E Hënë', 'E Martë', 'E Mërkurë', 'E Enjte', 'E Premte', 'E Shtunë'];
    const months = ['Janar', 'Shkurt', 'Mars', 'Prill', 'Maj', 'Qershor', 'Korrik', 'Gusht', 'Shtator', 'Tetor', 'Nëntor', 'Dhjetor'];
    
    const dayName = days[date.getDay()];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    return `${dayName}, ${day} ${month} ${year}`;
  };

  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      times.push(`${hour.toString().padStart(2, '0')}:00`);
      times.push(`${hour.toString().padStart(2, '0')}:30`);
    }
    return times;
  };

  const clearAll = () => {
    setSearchDate('');
    setSearchTime('');
    setAvailabilityResults(null);
  };

  // Set today's date as default
  useEffect(() => {
    if (!searchDate) {
      setSearchDate(new Date().toISOString().split('T')[0]);
    }
  }, [searchDate]);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-50 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-8 w-full max-w-sm sm:max-w-xl shadow-2xl border border-gray-100 animate-in slide-in-from-top-4 duration-300 max-h-[95vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4 sm:mb-8">
          <div className="flex items-center space-x-2 sm:space-x-3 flex-1">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0">
              <span className="text-xl sm:text-2xl">📅</span>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-xl sm:text-3xl font-bold text-gray-800 leading-tight">Kontrollo Disponueshmërinë</h3>
              <p className="text-gray-500 text-xs sm:text-sm mt-1">Kontrollues i shpejtë i kohës së lirë</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg sm:rounded-xl transition-all duration-200 flex-shrink-0 ml-2"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Availability Check Form */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl sm:rounded-2xl p-4 sm:p-8 border-2 border-green-200 mb-4 sm:mb-8">
          <div className="space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-6">
            <div>
              <label className="block text-sm sm:text-lg font-semibold text-gray-700 mb-2 sm:mb-3 flex items-center">
                <span className="mr-2">📅</span>
                Zgjidh Datën
              </label>
              <input
                type="date"
                value={searchDate}
                onChange={(e) => setSearchDate(e.target.value)}
                className="w-full p-3 sm:p-4 text-base sm:text-lg border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white transition-all duration-200 font-medium"
              />
            </div>
            <div>
              <label className="block text-sm sm:text-lg font-semibold text-gray-700 mb-2 sm:mb-3 flex items-center">
                <span className="mr-2">🕐</span>
                Zgjidh Orën
              </label>
              <select
                value={searchTime}
                onChange={(e) => setSearchTime(e.target.value)}
                className="w-full p-3 sm:p-4 text-base sm:text-lg border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white transition-all duration-200 font-medium"
              >
                <option value="">Zgjidh kohën...</option>
                {generateTimeOptions().map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Clear Button */}
          {(searchDate || searchTime) && (
            <div className="mt-4 sm:mt-6 text-center">
              <button
                onClick={clearAll}
                className="px-4 sm:px-6 py-2 text-sm sm:text-base text-gray-600 hover:text-gray-800 hover:bg-white rounded-lg sm:rounded-xl transition-all duration-200 font-medium"
              >
                Pastro Zgjedhjen
              </button>
            </div>
          )}
        </div>

        {/* Results Area */}
        <div>
          {availabilityResults ? (
            <div className={`rounded-xl sm:rounded-2xl p-4 sm:p-8 border-2 ${
              availabilityResults.isAvailable 
                ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-300' 
                : 'bg-gradient-to-br from-red-50 to-orange-50 border-red-300'
            }`}>
              <div className="text-center">
                <div className="text-4xl sm:text-6xl mb-3 sm:mb-4">
                  {availabilityResults.isAvailable ? '✅' : '❌'}
                </div>
                <h4 className="text-lg sm:text-2xl font-bold mb-2 sm:mb-3 text-gray-800 leading-tight">
                  {formatFullDate(availabilityResults.date)}
                </h4>
                <div className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 flex items-center justify-center space-x-2">
                  <span>🕐</span>
                  <span>{availabilityResults.time}</span>
                </div>
                
                {availabilityResults.isAvailable ? (
                  <div>
                    <div className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 rounded-full text-sm sm:text-lg font-bold bg-green-500 text-white shadow-lg mb-3 sm:mb-4">
                      ✨ E LIRË
                    </div>
                    <p className="text-green-700 text-sm sm:text-lg font-medium px-2">Kjo kohë është e lirë dhe gati për rezervim!</p>
                  </div>
                ) : (
                  <div>
                    <div className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 rounded-full text-sm sm:text-lg font-bold bg-red-500 text-white shadow-lg mb-3 sm:mb-4">
                      🚫 ZËNË
                    </div>
                    <div className="text-red-700 px-2">
                      <p className="text-base sm:text-lg font-bold mb-1 sm:mb-2 break-words">Rezervuar nga: {availabilityResults.reservation.clientName}</p>
                      <p className="text-xs sm:text-sm opacity-75">
                        Provoni të sugjeroni një kohë tjetër
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 sm:py-12">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <span className="text-2xl sm:text-3xl">🔍</span>
              </div>
              <p className="text-gray-500 font-semibold text-base sm:text-lg">Gati për të kontrolluar disponueshmërinë</p>
              <p className="text-gray-400 mt-1 sm:mt-2 text-sm sm:text-base px-4">Zgjidh një datë dhe orë për të parë nëse është e lirë</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Search; 